// CSV Export Utility Functions

export interface ExportData {
  [key: string]: any;
}

/**
 * Converts an array of objects to CSV format
 * @param data Array of objects to convert to CSV
 * @param filename Name of the file to download
 * @param headers Optional custom headers mapping
 */
export const exportToCSV = (
  data: ExportData[],
  filename: string,
  headers?: { [key: string]: string }
): void => {
  if (!data || data.length === 0) {
    alert('No data to export');
    return;
  }

  // Get all unique keys from the data
  const allKeys = Array.from(
    new Set(data.flatMap(item => Object.keys(item)))
  );

  // Create header row
  const headerRow = allKeys.map(key => {
    const header = headers?.[key] || key;
    return `"${header.replace(/"/g, '""')}"`;
  }).join(',');

  // Create data rows
  const dataRows = data.map(item => {
    return allKeys.map(key => {
      const value = item[key];
      if (value === null || value === undefined) {
        return '""';
      }
      
      // Convert value to string and escape quotes
      const stringValue = String(value).replace(/"/g, '""');
      return `"${stringValue}"`;
    }).join(',');
  });

  // Combine header and data rows
  const csvContent = [headerRow, ...dataRows].join('\n');

  // Create and download file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};

/**
 * Export drivers data to CSV
 */
export const exportDriversToCSV = (drivers: any[]): void => {
  const headers = {
    name: 'Driver Name',
    email: 'Email',
    phoneNumber: 'Phone Number',
    address: 'Address',
    licenseNumber: 'License Number',
    status: 'Status',
    assignedVehicles: 'Assigned Vehicles',
    lastTrip: 'Last Trip'
  };

  exportToCSV(drivers, 'drivers-export', headers);
};

/**
 * Export vehicles data to CSV
 */
export const exportVehiclesToCSV = (vehicles: any[]): void => {
  const headers = {
    vehicleNumber: 'Vehicle Number',
    vehicleType: 'Vehicle Type',
    vehicleInsurance: 'Insurance Company',
    vehicleInsuranceNumber: 'Insurance Number',
    vehicleInsuranceExpiry: 'Insurance Expiry',
    vehiclePermit: 'Permit Type',
    vehiclePermitNumber: 'Permit Number',
    vehiclePermitExpiry: 'Permit Expiry',
    vehiclePollution: 'Pollution Certificate',
    vehiclePollutionNumber: 'Pollution Number',
    vehiclePollutionExpiry: 'Pollution Expiry',
    vehicleRc: 'RC Type',
    vehicleRCNumber: 'RC Number',
    vehicleRcExpiry: 'RC Expiry',
    capacity: 'Capacity',
    status: 'Status',
    assignedDriver: 'Assigned Driver',
    lastMaintenance: 'Last Maintenance'
  };

  exportToCSV(vehicles, 'vehicles-export', headers);
};

/**
 * Export reports data to CSV
 */
export const exportReportsToCSV = (reports: any[], reportType: string): void => {
  const headers = {
    date: 'Date',
    driverName: 'Driver Name',
    vehicleNumber: 'Vehicle Number',
    tripId: 'Trip ID',
    startLocation: 'Start Location',
    endLocation: 'End Location',
    distance: 'Distance (km)',
    duration: 'Duration',
    temperature: 'Temperature (°C)',
    status: 'Status',
    notes: 'Notes'
  };

  exportToCSV(reports, `${reportType}-export`, headers);
};

/**
 * Export dashboard metrics to CSV
 */
export const exportDashboardToCSV = (metrics: any): void => {
  const data = [
    { metric: 'Active Drivers', value: metrics.activeDrivers || 0 },
    { metric: 'Fleet Vehicles', value: metrics.fleetVehicles || 0 },
    { metric: 'Cold Chain Deliveries', value: metrics.coldChainDeliveries || 0 },
    { metric: 'Temperature Violations', value: metrics.temperatureViolations || 0 }
  ];

  const headers = {
    metric: 'Metric',
    value: 'Value'
  };

  exportToCSV(data, 'dashboard-metrics', headers);
};
